import React, { useState } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const STATUS_CONFIG = {
  confirmed: { label: "Confirmed", color: "var(--color-success)", bg: "rgba(5,150,105,0.1)" },
  pending: { label: "Pending", color: "var(--color-warning)", bg: "rgba(217,119,6,0.1)" },
  called: { label: "Called", color: "var(--color-primary)", bg: "rgba(59,76,76,0.1)" },
  distributed: { label: "Distributed", color: "var(--color-secondary)", bg: "rgba(59,76,76,0.15)" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG?.[status] || STATUS_CONFIG?.pending;
  return (
    <span
      className="font-caption text-xs px-2 py-0.5 rounded-full font-medium"
      style={{ background: cfg?.bg, color: cfg?.color }}
    >
      {cfg?.label}
    </span>
  );
}

export default function CommitmentsPanel({ commitments = [], onCapitalCall }) {
  const [expanded, setExpanded] = useState(null);

  const fmt = (n) =>
    new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 })?.format(n);

  const gpRow         = commitments?.find((c) => c?.commitmentType === 'gp_allocation');
  const coInvestorRows = commitments?.filter((c) => c?.commitmentType !== 'gp_allocation');
  const gpTotal       = gpRow?.amount ?? 0;
  const coTotal       = coInvestorRows?.reduce((s, c) => s + c?.amount, 0) ?? 0;

  const renderRow = (c, isGP = false) => (
    <div
      key={c?.id}
      className="border border-border rounded-lg overflow-hidden"
      style={isGP ? { borderColor: 'var(--color-primary)', background: 'var(--color-primary-subtle)' } : {}}
    >
      <button
        className="w-full flex items-center gap-3 p-3 hover:bg-muted transition-all duration-200 text-left"
        onClick={() => setExpanded(expanded === c?.id ? null : c?.id)}
      >
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-caption font-semibold text-xs text-primary-foreground"
          style={{ background: isGP ? 'var(--color-primary)' : 'var(--color-secondary)' }}
        >
          {isGP ? <Icon name="Landmark" size={13} color="#fff" /> : c?.initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="font-caption text-sm font-medium text-foreground truncate">{c?.investor}</p>
            {isGP && (
              <span
                className="text-xs font-caption px-1.5 py-0.5 rounded shrink-0"
                style={{ background: 'var(--color-primary)', color: '#fff', fontSize: '0.6rem' }}
              >
                FUND
              </span>
            )}
          </div>
          <p className="font-data text-xs text-muted-foreground">{fmt(c?.amount)}</p>
        </div>
        <StatusBadge status={c?.status} />
        <Icon name={expanded === c?.id ? "ChevronUp" : "ChevronDown"} size={14} color="var(--color-muted-foreground)" />
      </button>

      {expanded === c?.id && (
        <div className="px-3 pb-3 bg-muted border-t border-border space-y-2">
          <div className="grid grid-cols-2 gap-2 pt-2">
            <div>
              <p className="font-caption text-xs text-muted-foreground">Committed</p>
              <p className="font-data text-sm font-medium text-foreground">{fmt(c?.amount)}</p>
            </div>
            <div>
              <p className="font-caption text-xs text-muted-foreground">Called</p>
              <p className="font-data text-sm font-medium text-foreground">{fmt(c?.called)}</p>
            </div>
            <div>
              <p className="font-caption text-xs text-muted-foreground">Distributed</p>
              <p className="font-data text-sm font-medium text-foreground">{fmt(c?.distributed)}</p>
            </div>
            <div>
              <p className="font-caption text-xs text-muted-foreground">Ownership</p>
              <p className="font-data text-sm font-medium text-foreground">{c?.ownership}%</p>
            </div>
          </div>
          <div>
            <p className="font-caption text-xs text-muted-foreground">Last Call Date</p>
            <p className="font-caption text-sm text-foreground">{c?.lastCallDate}</p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="card flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-base font-semibold text-foreground">Commitments</h3>
        {onCapitalCall && (
          <Button variant="default" size="sm" iconName="PhoneCall" iconPosition="left" iconSize={14} onClick={onCapitalCall}>
            Capital Call
          </Button>
        )}
      </div>

      {/* Capital summary line — only when both GP and co-investor rows present */}
      {gpRow && coInvestorRows?.length > 0 && (
        <div
          className="flex items-center gap-3 px-3 py-2 rounded-lg mb-3 text-xs font-caption"
          style={{ background: 'var(--color-muted)' }}
        >
          <Icon name="Info" size={12} color="var(--color-muted-foreground)" />
          <span style={{ color: 'var(--color-muted-foreground)' }}>
            <span className="font-medium text-foreground">{fmt(gpTotal)}</span> GP Fund Allocation ·{' '}
            <span className="font-medium text-foreground">{fmt(coTotal)}</span> Co-investors
          </span>
        </div>
      )}

      <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
        {/* GP allocation row first, highlighted */}
        {gpRow && renderRow(gpRow, true)}

        {/* Separator */}
        {gpRow && coInvestorRows?.length > 0 && (
          <div className="flex items-center gap-2 py-1">
            <div className="flex-1 h-px" style={{ background: 'var(--color-border)' }} />
            <span className="text-xs font-caption shrink-0" style={{ color: 'var(--color-muted-foreground)' }}>Co-investors</span>
            <div className="flex-1 h-px" style={{ background: 'var(--color-border)' }} />
          </div>
        )}

        {/* Co-investor rows */}
        {coInvestorRows?.map((c) => renderRow(c, false))}

        {/* Fallback: render all rows if no commitmentType set */}
        {!gpRow && coInvestorRows?.length === 0 && commitments?.map((c) => renderRow(c, false))}
      </div>

      <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
        <span className="font-caption text-xs text-muted-foreground">{commitments?.length} investors</span>
        <span className="font-data text-sm font-semibold text-foreground">
          {fmt(commitments?.reduce((s, c) => s + c?.amount, 0))} total
        </span>
      </div>
    </div>
  );
}